import i18nFactory from '@yandex-int/i18n';
import { isEqual, uniq } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    mapPhraseWithMinusWordsToString,
    mapStringToPhraseWithMinusWords
} from '../../service/PhrasesHelpers/PhrasesHelpers';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import {
    PhraseAdjustment,
    PhraseAdjustmentItem
} from '../PhraseAdjustment/PhraseAdjustment';
import { PhraseEditor } from '../PhraseEditor/PhraseEditor';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';

import './PhraseEditPopup.css';
import * as keyset from './PhraseEditPopup.i18n';

const i18n = i18nFactory(keyset);

export interface PhraseEditPopupProps {
    value: string;
    error?: string;
    onSave: () => void;
    onCancel: () => void;
    onChange: (value: string) => void;
    anchor: React.ReactNode;
    isInProgress: boolean;
    isGridScrolling: boolean;
    regionIds: number[];
    switchAdjustmentVisibility: (visible: boolean) => void;
    updateAdjustmentForecast: (value: string) => void;
    loadForecastData: (phraseWithMinusWords: string) => void;
    onAdjustmentWillUnmount: () => void;
    gridChangeAdjustmentData: (items: PhraseAdjustmentItem[]) => void;
    adjustmentData: {
        visible: boolean;
        showsForecast?: number;
        hasMoreItems: boolean;
        items?: PhraseAdjustmentItem[];
    };
}

interface PhraseEditPopupState {
    error?: string;
}

export class PhraseEditPopup extends React.PureComponent<
    PhraseEditPopupProps,
    PhraseEditPopupState
> {
    state = {
        error: undefined
    };

    componentWillReceiveProps(nextProps: PhraseEditPopupProps) {
        // Если нет ошибок и нет загрузки - сохранение прошло успешно - закрываем попап
        if (
            (!nextProps.error &&
                this.props.isInProgress &&
                !nextProps.isInProgress) ||
            nextProps.isGridScrolling
        ) {
            this.props.onCancel();
        }

        if (!isEqual(nextProps.error, this.props.error)) {
            this.setState({
                error: nextProps.error
            });
        }
    }

    render() {
        const { value, onCancel, anchor, onSave, isInProgress } = this.props;

        const isAcceptButtonEnabled = value !== '';

        const { error } = this.state;

        if (!anchor) {
            return null;
        }

        return (
            <PopupDecorator
                onOutsideClick={onCancel}
                anchor={anchor}
                visible={true}
                cls={Bem.cls('phrase-edit-popup')}
                directions={[
                    'right-top',
                    'left-center',
                    'right-bottom',
                    'left-bottom',
                    'top-left'
                ]}
                hasTail={true}
                target="anchor"
                header={this.getHeader()}
            >
                <PhraseEditor
                    value={value}
                    error={error}
                    onChange={this.onChange}
                />

                <div className={Bem.cls('phrase-edit-popup', 'buttons')}>
                    <ButtonDecorator
                        theme={'action'}
                        view="default"
                        tone="default"
                        size={'n'}
                        text={i18n('set')}
                        cls={Bem.cls('phrase-edit-popup', 'button-accept')}
                        progress={isInProgress}
                        onClick={onSave}
                        disabled={!isAcceptButtonEnabled}
                    />

                    <ButtonDecorator
                        theme={'pseudo'}
                        view="default"
                        tone="default"
                        size={'n'}
                        text={i18n('cancel')}
                        cls={Bem.cls('phrase-edit-popup', 'button-cancel')}
                        onClick={onCancel}
                    />

                    <ButtonDecorator
                        theme={'pseudo'}
                        view="default"
                        tone="default"
                        size={'n'}
                        text={i18n('adjust')}
                        cls={Bem.cls('phrase-edit-popup', 'button-adjust')}
                        onClick={this.onAdjustmentClick}
                    />
                </div>
                {this.getPhraseAdjustment()}
            </PopupDecorator>
        );
    }

    private getHeader = () => i18n('phrase-with-minus-words');
    private getPhraseAdjustment = () => {
        const { adjustmentData } = this.props;

        if (!adjustmentData.visible) {
            return null;
        }

        return (
            <PhraseAdjustment
                cls={Bem.cls('phrase-edit-popup', 'phrase-adjustment')}
                showsForecast={adjustmentData.showsForecast}
                hasMoreItems={adjustmentData.hasMoreItems}
                items={adjustmentData.items}
                onChange={this.onPhraseAdjustmentChange}
                onComponentDidMount={this.onAdjustPhraseComponentDidMount}
                onComponentWillUnmount={this.onAdjustmentWillUnmount}
                onLoadMoreClick={this.onLoadMoreClick}
            />
        );
    };

    private onPhraseAdjustmentChange = (items: PhraseAdjustmentItem[]) => {
        const { gridChangeAdjustmentData, value } = this.props;

        const { phrase, minusWords } = mapStringToPhraseWithMinusWords(value);

        gridChangeAdjustmentData(items);

        const { uncheckedMinusWords, checkedMinusWords } = items.reduce(
            (
                acc: {
                    uncheckedMinusWords: string[];
                    checkedMinusWords: string[];
                },
                el
            ) => {
                if (el.checked) {
                    acc.checkedMinusWords.push(el.minusWord);
                } else {
                    acc.uncheckedMinusWords.push(el.minusWord);
                }
                return acc;
            },
            { uncheckedMinusWords: [], checkedMinusWords: [] }
        );

        const newMinusWords = uniq([
            ...minusWords.filter(el => !uncheckedMinusWords.includes(el)),
            ...checkedMinusWords
        ]);

        this.onChange(mapPhraseWithMinusWordsToString(phrase, newMinusWords));
    };

    private onAdjustmentClick = () => {
        this.props.switchAdjustmentVisibility(
            !this.props.adjustmentData.visible
        );
        this.props.updateAdjustmentForecast(this.props.value);
    };

    private onChange = (value: string) => {
        this.props.onChange(value);
        this.props.updateAdjustmentForecast(value);
    };

    private onAdjustPhraseComponentDidMount = () => {
        this.props.loadForecastData(this.props.value);
    };

    private onAdjustmentWillUnmount = () => {
        this.props.onAdjustmentWillUnmount();
    };

    private onLoadMoreClick = () => {
        const { value, adjustmentData } = this.props;
        const { phrase, minusWords } = mapStringToPhraseWithMinusWords(value);

        const extendedMinusWords = uniq([
            ...minusWords,
            ...(adjustmentData.items || []).map(el => el.minusWord)
        ]);

        this.props.loadForecastData(
            mapPhraseWithMinusWordsToString(phrase, extendedMinusWords)
        );
    };
}
